import type { Movie, NewsArticle } from '@/types/content';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';

export function getAllMovies(): Movie[] {
  return movies;
}

export function getAllNews(): NewsArticle[] {
  return news;
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function getNewsById(id: string): NewsArticle | undefined {
  return news.find((article) => article.id === id);
}

export function getRelatedMovies(movie: Movie, limit = 6): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.director === movie.director) score += 3;
      score += item.genres.filter((g) => movie.genres.includes(g)).length * 2;
      if (item.region === movie.region) score += 1;
      if (item.type === movie.type) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.item);
}

export function getRelatedNews(movie: Movie, limit = 3): NewsArticle[] {
  const withScore = news.map((article) => {
    let score = 0;
    if (article.title.includes(movie.title)) score += 5;
    movie.genres.forEach((g) => {
      if (article.tags.some((t) => t.includes(g))) score += 2;
    });
    return { article, score };
  });
  const matched = withScore.filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
  const pool = matched.length > 0 ? matched : withScore;
  return pool
    .sort((a, b) => (a.score === b.score ? b.article.views - a.article.views : b.score - a.score))
    .slice(0, limit)
    .map((s) => s.article);
}

export interface SearchResult {
  movieResults: Movie[];
  newsResults: NewsArticle[];
}

export function searchContent(keyword: string): SearchResult {
  const key = keyword.trim().toLowerCase();
  if (!key) return { movieResults: [], newsResults: [] };

  const movieResults = movies.filter((movie) =>
    [movie.title, movie.originalTitle, movie.director, movie.type, movie.region, ...movie.genres, ...movie.actors]
      .join(' ')
      .toLowerCase()
      .includes(key),
  );

  const newsResults = news.filter((article) =>
    [article.title, article.excerpt, article.category, ...article.tags]
      .join(' ')
      .toLowerCase()
      .includes(key),
  );

  return { movieResults, newsResults };
}

export function formatDate(date: string): string {
  return date.replace(/-/g, '.');
}